import { useEffect, useRef, useState } from 'react';
import { Bus, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useMapStore } from '../../stores/mapStore';
import { MAP_FOCUS_ZOOM } from '../map/config/mapConfig';
import { TransportStopListItem } from './TransportStopListItem';
import { fmtDist, stopTypeLabel, type TransportStopFeature } from './transportStopUtils';
import { useNearbyTransportStops } from './useNearbyTransportStops';

const RADIUS_OPTIONS = [
  { label: '500 m', value: 500 },
  { label: '1 km', value: 1000 },
  { label: '2 km', value: 2000 },
  { label: '5 km', value: 5000 },
] as const;

export interface TransportNearMeProps {
  onNavigateToStop?: (name: string, coords: { lat: number; lng: number }) => void;
  /** Increment to programmatically expand the section (e.g. from a chip). */
  forceOpen?: number;
}

export function TransportNearMe({ onNavigateToStop, forceOpen = 0 }: TransportNearMeProps) {
  const { flyToLocation, setSheetState, setSelectedStopCoords, warnOnLocationFeatureUse } = useMapStore();
  const [radius, setRadius] = useState<number>(2000);
  const [expanded, setExpanded] = useState(false);
  const prevForceOpenRef = useRef(0);

  const { anchor, loading, error, sortedStops, reload } = useNearbyTransportStops(radius, true);

  useEffect(() => {
    if (forceOpen > 0 && forceOpen !== prevForceOpenRef.current) {
      prevForceOpenRef.current = forceOpen;
      setExpanded(true);
      warnOnLocationFeatureUse();
      setSheetState('half');
    }
  }, [forceOpen, setSheetState, warnOnLocationFeatureUse]);

  useEffect(() => {
    warnOnLocationFeatureUse();
  }, [warnOnLocationFeatureUse]);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) {
      warnOnLocationFeatureUse();
      setSheetState('half');
    }
  };

  const handleStopClick = (stop: TransportStopFeature) => {
    const [lng, lat] = stop.geometry.coordinates;
    const name = stop.properties?.name || stopTypeLabel(stop.properties?.type);
    setSelectedStopCoords({ lat, lng, name });
    flyToLocation({ lat, lng }, MAP_FOCUS_ZOOM);
    setSheetState('collapsed');
  };

  const handleNavigate = (stop: TransportStopFeature) => {
    const [lng, lat] = stop.geometry.coordinates;
    const name = stop.properties?.name || stopTypeLabel(stop.properties?.type);
    onNavigateToStop?.(name, { lat, lng });
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
          <Bus size={20} className="text-emerald-700 dark:text-emerald-300" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Transport near you</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
            {loading
              ? 'Finding nearby stops…'
              : sortedStops.length > 0
                ? `${sortedStops.length} verified stop${sortedStops.length === 1 ? '' : 's'} within ${fmtDist(radius)}`
                : anchor
                  ? `No verified stops within ${fmtDist(radius)}`
                  : 'Enable location · trotro · bus · taxi · rail'}
          </p>
        </div>
        {expanded
          ? <ChevronUp size={18} className="text-gray-400 flex-shrink-0" />
          : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-800">
          <div className="px-4 pt-3 pb-2 flex items-center gap-2">
            <div className="flex gap-1.5 flex-wrap flex-1">
              {RADIUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRadius(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    radius === opt.value
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => reload()}
              disabled={loading}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-40"
              title="Refresh"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          <div className="px-3 pb-3 space-y-1.5 max-h-72 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {!loading && error && (
              <p className="text-xs text-red-500 dark:text-red-400 px-1 py-2 text-center">{error}</p>
            )}

            {!loading && !error && sortedStops.length === 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                {anchor
                  ? `No verified stops within ${fmtDist(radius)}.`
                  : 'Enable location to find nearby stops.'}
              </p>
            )}

            {!loading &&
              !error &&
              sortedStops.slice(0, 30).map((stop) => (
                <TransportStopListItem
                  key={String(stop.properties?.id ?? stop.id)}
                  stop={stop}
                  anchor={anchor}
                  onSelect={handleStopClick}
                  onNavigate={onNavigateToStop ? handleNavigate : undefined}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
